import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useDebounceSearch } from '@blaze-cms/plugin-search-ui';
import SearchContentResults from './SearchContentResults';
import SearchContentToggleIcon from './SearchContentToggleIcon';
import { useAppSyncEventHook } from '../../hooks';
const SearchContent = props => {
  const {
    entities,
    searchInputWrapperMobile,
    searchInputWrapperDesktop,
    isMobile,
    placeholder,
    modifier,
    collapsedSearch,
    searchUrl,
    navigateToSingleSearchResult
  } = props;
  const searchContentRef = useRef(null);
  const [collapsed, setCollapsed] = useState(collapsedSearch);
  const [showResults, setShowResults] = useState(false);
  const router = useRouter();
  const {
    runHook
  } = useAppSyncEventHook({
    eventName: 'searchcontent:query-variables',
    props,
    returnFunction: true
  });
  const {
    results,
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm
  } = useDebounceSearch({
    entities,
    initialSearchTerm: '',
    resultKeys: 'id, name, image { url }, url',
    queryHook: runHook
  });
  useEffect(() => {
    const handleRouteChange = () => {
      setSearchTerm('');
      setShowResults(false);
      if (collapsedSearch) setCollapsed(true);
    };
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [collapsedSearch, router.events, setSearchTerm]);
  useEffect(() => {
    const handleClickOutside = event => {
      if (searchContentRef.current && !searchContentRef.current.contains(event.target)) {
        setShowResults(false);
        setSearchTerm('');
        if (collapsedSearch && document.activeElement !== searchContentRef.current.querySelector('input')) {
          setCollapsed(true);
        }
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [collapsedSearch, setSearchTerm]);
  const responsiveClasses = isMobile ? searchInputWrapperMobile : searchInputWrapperDesktop;
  const handleNavigation = (e, url) => {
    e.preventDefault();
    router.push(url);
  };
  const handleKeyDown = e => {
    if (e.key === 'Enter' && e.target.value !== '') {
      if (navigateToSingleSearchResult && results && results.length === 1 && results[0].url) {
        router.push(results[0].url);
      } else {
        const searchUrlWithoutHash = (searchUrl || '/search').split('#')[0];
        router.push(`${searchUrlWithoutHash}?search_term=${e.target.value}`);
      }
    }
  };
  const handleIconClick = () => {
    const isCloseIcon = collapsedSearch ? !collapsed : debouncedSearchTerm;
    if (collapsedSearch) setCollapsed(!collapsed);
    if (isCloseIcon) {
      setSearchTerm('');
      setShowResults(false);
    } else {
      setShowResults(true);
    }
  };
  const className = ['search-content', modifier, responsiveClasses].filter(Boolean).join(' ');
  const searchModifier = collapsed ? 'search-content--collapse' : 'search-content--expanded';
  return /*#__PURE__*/React.createElement("div", {
    className: className,
    ref: searchContentRef
  }, /*#__PURE__*/React.createElement("div", {
    className: `${searchModifier}__wrapper`
  }, /*#__PURE__*/React.createElement("label", {
    className: `${searchModifier}__label`
  }, /*#__PURE__*/React.createElement("span", {
    className: `${searchModifier}__icon_wrapper`
  }, /*#__PURE__*/React.createElement(SearchContentToggleIcon, {
    searchTerm: debouncedSearchTerm,
    onClick: handleIconClick,
    useCloseIcon: collapsedSearch ? !collapsed : debouncedSearchTerm
  })), !collapsed && /*#__PURE__*/React.createElement("input", {
    className: `${searchModifier}__input`,
    type: "text",
    name: "search",
    placeholder: placeholder,
    value: searchTerm,
    onChange: e => setSearchTerm(e.target.value),
    onKeyDown: handleKeyDown,
    onFocus: () => setShowResults(true),
    "data-testid": "search-content-input"
  }))), showResults && /*#__PURE__*/React.createElement(SearchContentResults, {
    results: results,
    debouncedSearchTerm: debouncedSearchTerm,
    handleClick: handleNavigation
  }));
};
SearchContent.propTypes = {
  searchInputWrapperMobile: PropTypes.string,
  searchInputWrapperDesktop: PropTypes.string,
  placeholder: PropTypes.string,
  isMobile: PropTypes.bool,
  entities: PropTypes.array.isRequired,
  modifier: PropTypes.string,
  collapsedSearch: PropTypes.bool,
  searchUrl: PropTypes.string,
  navigateToSingleSearchResult: PropTypes.bool
};
SearchContent.defaultProps = {
  searchInputWrapperMobile: '',
  searchInputWrapperDesktop: '',
  placeholder: '',
  isMobile: false,
  modifier: '',
  collapsedSearch: false,
  searchUrl: '',
  navigateToSingleSearchResult: false
};
export default SearchContent;
//# sourceMappingURL=SearchContent.js.map